import apiClient, { extractData, handleApiError } from './api.js';
import axios from 'axios';

const BILLING_PUBLIC_BASE_URL = `${apiClient.defaults.baseURL}/billing`;

export class BillingService {
  // ===========================================================================
  // POS-Facing Endpoints (require Karobar user JWT)
  // ===========================================================================

  /**
   * Create a payment session for an order (generates QR URL)
   * POST /api/v1/billing/sessions
   * @param {string} orderId
   * @param {string} outletId
   * @returns {Promise<{session_token: string, qr_url: string, expires_at: string}>}
   */
  static async createPaymentSession(orderId, outletId) {
    try {
      const response = await apiClient.post('/billing/sessions', {
        order_id: orderId,
        outlet_id: outletId,
      });
      return extractData(response);
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Poll recent payment session statuses for an outlet
   * GET /api/v1/billing/sessions/status?outlet_id=...
   * @param {string} outletId
   * @returns {Promise<Array>}
   */
  static async getSessionStatuses(outletId) {
    try {
      const response = await apiClient.get('/billing/sessions/status', {
        params: { outlet_id: outletId },
      });
      return extractData(response) || [];
    } catch (error) {
      console.error('Error getting session statuses:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Manually expire/cancel a payment session
   * POST /api/v1/billing/sessions/{session_token}/expire
   * @param {string} sessionToken
   * @returns {Promise<void>}
   */
  static async expireSession(sessionToken) {
    try {
      await apiClient.post(`/billing/sessions/${sessionToken}/expire`);
    } catch (error) {
      console.error('Error expiring session:', error);
      throw new Error(handleApiError(error));
    }
  }

  // ===========================================================================
  // Customer-Facing Endpoints (no Karobar JWT required)
  // ===========================================================================

  /**
   * Fetch the bill details for a customer (used on the pay page)
   * GET /api/v1/billing/s/{session_token}
   * @param {string} sessionToken
   * @returns {Promise<Object>}
   */
  static async fetchBill(sessionToken) {
    try {
      const response = await axios.get(
        `${BILLING_PUBLIC_BASE_URL}/s/${sessionToken}`
      );
      if (response.data?.success === false) {
        throw new Error(response.data?.error?.message || 'Failed to fetch bill');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching bill:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Initiate or confirm payment for a session
   * POST /api/v1/billing/s/{session_token}/pay
   * @param {string} sessionToken
   * @param {string} bearerToken - short-lived token from bill fetch
   * @param {Object} paymentData - { action: "initiate"|"confirm", transaction_id?, payment_method? }
   * @returns {Promise<Object>}
   */
  static async processPayment(sessionToken, bearerToken, paymentData) {
    try {
      const response = await axios.post(
        `${BILLING_PUBLIC_BASE_URL}/s/${sessionToken}/pay`,
        paymentData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      if (response.data?.success === false) {
        throw new Error(response.data?.error?.message || 'Payment failed');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw new Error(handleApiError(error));
    }
  }

  // ===========================================================================
  // Public Endpoints
  // ===========================================================================

  /**
   * Get a permanent receipt (no auth required)
   * GET /api/v1/billing/r/{receipt_id}
   * @param {string} receiptId
   * @returns {Promise<Object>}
   */
  static async fetchReceipt(receiptId) {
    try {
      const response = await axios.get(
        `${BILLING_PUBLIC_BASE_URL}/r/${receiptId}`
      );
      if (response.data?.success === false) {
        throw new Error(response.data?.error?.message || 'Receipt not found');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching receipt:', error);
      throw new Error(handleApiError(error));
    }
  }
}
